import { FC, ReactNode, useCallback } from 'react';
import { useForm, Controller, FormProvider, FormState } from 'react-hook-form';
import { Grid, TextField, Stack, FormControlLabel, Typography } from '@mui/material';
import { subYears } from 'date-fns';

import { useDictionary } from '@cvt/hooks/useDictionary';
import { useFormPersist, clearKeys } from '@cvt/hooks/form/useFormPersist';

import { getPossibleDate } from '@shared/helpers/date';

import { validateBirthdayDate, validatePassingDate, validatePetName } from '@modules/Tributes/helpers/validators';

import { PetTypeSelect } from './PetTypeSelect';
import { PetSexSelect } from './PetSexSelect';
import { DateField } from './Field/DateField';

export type FormData = {
    petName?: string;
    petType?: string;
    sex?: string;
    petDescription?: string;
    petDateOfBirth?: CVT.MaybeNull<Date>;
    petDateOfPassing?: CVT.MaybeNull<Date>;
    firstName?: string;
    email?: string;
    phone?: string;
};

interface Props {
    onSubmitRequest: (values: FormData) => void | Promise<void>;
    onFieldChange?: (fieldName: keyof FormData, value: any) => void;
    slots?: {
        footer?: ReactNode | ((formState: FormState<FormData>) => ReactNode);
    };
    defaultValues?: Partial<FormData>;
}

const DEFAULT_VALUES: Partial<FormData> = {
  petName: '',
  petType: '',
  sex: '',
  petDescription: '',
  petDateOfBirth: null,
  petDateOfPassing: null,
  firstName: '',
  email: '',
  phone: '',
};

export const datePickerRange = {
  minDate: subYears(new Date(), 150),
  maxDate: new Date(),
};

const transformOutput = (value: FormData): FormData => ({
  ...value,
  petDateOfBirth: getPossibleDate(value.petDateOfBirth),
  petDateOfPassing: getPossibleDate(value.petDateOfPassing),
});

export const PetDetailsManualForm: FC<Props> = (props) => {
  const { onSubmitRequest, slots, defaultValues = DEFAULT_VALUES, onFieldChange } = props;
  const dictionary = useDictionary();
  const methods = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { handleSubmit, formState, watch, setValue } = methods;

  useFormPersist('pet-details', {
    isDirty: formState.isDirty,
    isSubmitSuccessful: formState.isSubmitSuccessful,
    watch,
    setValue,
    transformOutput,
  });

  const onSubmit = useCallback(
    async (data: FormData) => {
      clearKeys(['tribute-story']);
      return onSubmitRequest(data);
    },
    [onSubmitRequest],
  );

  return (
    <FormProvider {...methods}>
      <form style={{ width: '100%', height: '100%' }} onSubmit={handleSubmit(onSubmit)}>
        <Stack justifyContent="space-between" height="100%" gap={4}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Stack direction="row" alignItems="center" gap={1} mb={2}>
                <Typography fontSize={18} fontWeight={600}>
                  Pet Details
                </Typography>
              </Stack>
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                required
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="petName"
                    rules={{
                      required: dictionary.forms.validations.required,
                      validate: (value) => validatePetName(value, dictionary),
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        variant="outlined"
                        color="secondary"
                        placeholder="Enter your pet's name"
                        fullWidth
                        size="small"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          onFieldChange?.('petName', e.target.value);
                        }}
                        slotProps={{
                          input: {
                            sx: {
                              fieldset: {
                                borderColor: '#e2e8f0',
                                borderRadius: '10px',
                              },
                            },
                          },
                        }}
                      />
                    )}
                  />
                }
                label="Pet Name"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                required
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="petType"
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field, fieldState }) => (
                      <PetTypeSelect
                        {...field}
                        value={field.value || ''}
                        fullWidth
                        size="small"
                        label=""
                        onChange={(event) => {
                          const selectedValue = event.target.value;
                          field.onChange(selectedValue);
                          onFieldChange?.('petType', selectedValue);
                        }}
                        sx={{
                          fieldset: {
                            borderColor: '#e2e8f0',
                            borderRadius: '10px',
                          },
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                }
                label="Beloved Companion Type"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                required
                labelPlacement="top"
                sx={{
                  alignItems: 'flex-start',
                  color: '#263238',
                  m: 0,
                  width: '100%',
                  '.MuiFormControlLabel-label': {
                    fontSize: 14,
                    fontWeight: 500,
                    mb: 1,
                  },
                }}
                slotProps={{
                  typography: {
                    fontSize: 12,
                    fontWeight: 400,
                  },
                }}
                control={
                  <Controller
                    name="sex"
                    rules={{ required: dictionary.forms.validations.required }}
                    render={({ field, fieldState }) => (
                      <PetSexSelect
                        {...field}
                        value={field.value || ''}
                        fullWidth
                        size="small"
                        label=""
                        onChange={(event) => {
                          const selectedValue = event.target.value;
                          field.onChange(selectedValue);
                          onFieldChange?.('sex', selectedValue);
                        }}
                        sx={{
                          fieldset: {
                            borderColor: '#e2e8f0',
                            borderRadius: '10px',
                          },
                        }}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                }
                label="Pet Gender"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <DateField<FormData>
                name="petDateOfBirth"
                label={dictionary.forms.tributes.adoptionDate}
                maxDate={datePickerRange.maxDate}
                minDate={datePickerRange.minDate}
                rules={{
                  validate: (value) => {
                    return validateBirthdayDate({
                      value,
                      dictionary,
                      ...datePickerRange,
                    });
                  },
                }}
                onChange={(date) => onFieldChange?.('petDateOfBirth', date)}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }} alignSelf="end">
              <DateField<FormData>
                name="petDateOfPassing"
                label={dictionary.forms.tributes.passingDate}
                maxDate={datePickerRange.maxDate}
                minDate={datePickerRange.minDate}
                rules={{
                  validate: (passingDate, formValues) => {
                    return validatePassingDate({
                      value: passingDate,
                      birthday: formValues.petDateOfBirth,
                      dictionary,
                      ...datePickerRange,
                    });
                  },
                }}
                onChange={(date) => onFieldChange?.('petDateOfPassing', date)}
              />
            </Grid>
          </Grid>

          {typeof slots?.footer === 'function' ? slots?.footer(formState) : slots?.footer}
        </Stack>
      </form>
    </FormProvider>
  );
};
